import { useEffect, useRef, useState } from 'react';
import { useRouter } from 'next/router';
import { Paperclip } from 'lucide-react';
import { Button, Card, Input, PageLoader, Typography } from '../ui';
import {
  useContactEntryQuery,
  refreshContactData,
  contactPath,
  type ContactId,
  type WebOutreachAttachment,
  type WebOutreachThread,
} from '../../../lib/contact-book';
import { OutreachNoReplyNeeded } from '../tasks/OutreachNoReplyNeeded';
import {
  attachmentLabel,
  contactDisplayName,
  threadTurn,
  messageIsMine,
  senderLabel,
  shortRelativeTime,
  startsSenderRun,
  strippedMessageText,
  threadTitle,
} from '../../../lib/contact-rows';
import { Markdown } from '../../Markdown';
import { useMe } from '../../../lib/me';
import { useAgentName } from '../../../lib/use-agent-name';
import { useBasePath } from '../../../lib/use-base-path';
import { useGenericBi } from '../../../lib/use-generic-bi';
import { chatHandoffLink } from '../../../lib/chat-handoff';
import { outreachReplyMessage } from '../../../lib/outreach-reply-message';
import { useTranslation } from '../../../lib/i18n';
import { ChannelChip } from './ChannelChip';
import { ContactReadError } from './ContactReadError';
import { TurnBadge } from './TurnBadge';
import styles from './contacts.module.css';

/**
 * One outreach thread, in full.
 *
 * Reads the same person-scoped endpoint the contact page does and picks the thread out of it, so
 * arriving here directly — a deep link, a reload — costs one call and no extra route. The thread is
 * carried on that response already; there is nothing further to fetch.
 */
export function ContactThreadBody({ id, outreachId }: { id: ContactId; outreachId: string }) {
  const { t, i18n } = useTranslation();
  const router = useRouter();
  // The real query signals — see the note on `ContactDetailBody`: the data-only view cannot tell a failed
  // read from a pending one, so this page's loader never resolved on a contact-book failure.
  const { entry, isLoading, isError, refetch } = useContactEntryQuery(id);
  // Called BEFORE the early return below — a hook after a conditional return breaks the rules of
  // hooks the moment the loader branch is taken.
  const me = useMe();
  const agentName = useAgentName();

  // A correspondence opens at its END, like a chat: the newest message is what the reader came for.
  const lastRef = useRef<HTMLDivElement | null>(null);
  const [scrolled, setScrolled] = useState(false);
  useEffect(() => {
    if (entry && !scrolled && lastRef.current) {
      lastRef.current.scrollIntoView({ block: 'end' });
      setScrolled(true);
    }
  }, [entry, scrolled]);

  if (isLoading) return <PageLoader />;
  // A failed read must not claim the outreach "isn't here" — that is a statement about the reader's data
  // made out of our own failure. Only a SETTLED read with no such thread falls through to that copy below.
  if (!entry) return <ContactReadError canRetry={isError} onRetry={refetch} />;

  const thread = [...(entry.outbound ?? []), ...(entry.inbound ?? [])].find((candidate) => candidate.outreachId === outreachId);

  if (!thread) {
    return (
      <div className={styles.empty}>
        <Typography variant="p2">{t('contacts.thread.missing.title')}</Typography>
        <Typography variant="p3" color="secondary">
          {t('contacts.thread.missing.body')}
        </Typography>
      </div>
    );
  }

  const messages = thread.messages ?? [];
  // Same fallback the row/detail use, so the thread header, the transcript's "them" attribution, and
  // the reply handoff all name a recipient-only contact by its address rather than a blank.
  const theirName = contactDisplayName(entry.contact, entry.channels, t('contacts.row.unnamed'));
  // The ISA recipient's display name — the SAME name the outreach envelope writes ("Message from
  // Yonatan Nadel"), which is what makes matching it against `senderName` meaningful. The Wix
  // first/last is the fallback, and an empty one simply leaves `messageIsMine` on `fromMe`.
  const myName = (me?.recipientName || [me?.firstName, me?.lastName].filter(Boolean).join(' ')).trim();
  // The two ASSISTANTS are the speakers — this side's Maestro wrote the outbound messages, and
  // `entry.agentName` (off the counterparty's Recipient) wrote the replies. Either can be empty:
  // a counterparty who is not a Symphony user has no agent, and the label falls back to the person.
  const myAgentName = agentName.name;

  return (
    <div className={styles.detail}>
      <ThreadHeader thread={thread} name={theirName} />

      <div className={styles.threadFull}>
        {messages.length ? (
          messages.map((message, index) => {
            const mine = messageIsMine(message, myName);
            return (
              <div
                key={`${outreachId}-${index}`}
                className={mine ? styles.fullMessageMine : styles.fullMessageTheirs}
                ref={index === messages.length - 1 ? lastRef : undefined}
              >
                {/* The name goes on the FIRST bubble of a run only. Repeating it on every bubble is
                    what makes a two-party thread read as a form rather than a conversation. */}
                {/* Speaker and time on ONE meta line above the message — the transcript idiom.
                    The name is drawn once per run; the time on every row, because a record is read
                    for when things happened. */}
                <span className={styles.messageMeta}>
                  {startsSenderRun(messages, index) ? (
                    <Typography variant="p3" color="secondary" className={styles.messageSender}>
                      {senderLabel(message, {
                        me: myName,
                        them: theirName,
                        myAgent: myAgentName,
                        theirAgent: entry.agentName,
                      })}
                    </Typography>
                  ) : null}
                  <Typography variant="p3" color="secondary">
                    {shortRelativeTime(message.sentAt, undefined, t('contacts.time.now'))}
                  </Typography>
                </span>
                {/* The bodies are authored Markdown — the real ones carry headings and tables, and
                    rendering them as one flat string is what made the inline version unreadable. */}
                {/* The WORDS decide their direction (a Hebrew reply reads right-to-left); the meta line above
                    is Latin and stays put — `dir="auto"` on the whole row resolved LTR off the name. */}
                <div dir="auto" className={styles.messageBody}>
                  <Markdown>{strippedMessageText(message.text)}</Markdown>
                </div>
                <MessageAttachments attachments={message.attachments} />
              </div>
            );
          })
        ) : (
          // Not an empty state: the list projection omits messages, so this is what the screen looks
          // like if it is ever reached with a row from the browse rather than the person read.
          <Typography variant="p2">{strippedMessageText(thread.lastMessage)}</Typography>
        )}
      </div>

      <ThreadComposer contactName={theirName} subject={threadTitle(thread, i18n.language)} agentName={myAgentName} />

      {/* The other honest answer to a message: nothing. Mirrors the task-modal thread's own escape
          hatch (OutreachThreadModal) — this page is a genuinely separate component reading the same
          outreach data, and it had never grown the same way out (reported: "no way to close an
          outreach, I do not want to reply"). Gated the same way: any thread not already settled, not
          just the ones badged as awaiting a reply — an empty or unresolved-author thread is stuck the
          same way and would otherwise have no exit at all.

          ⚠️ This gate read `thread.status !== 'COMPLETED'` and therefore NEVER FIRED: symphony-bff
          lowercases the status on the way out (`Contacts.statusName` → `status.name().toLowerCase()`),
          so the field is `'completed'` and the comparison was true on every thread. Harmless while the
          header printed a raw status nobody read — the button was simply offered on finished threads,
          and `/api/outreach-complete` short-circuits an already-complete outreach. It stops being
          harmless now the header says "Replied" in so many words, because the screen would be closing
          a conversation it has just told you is closed. Goes through `threadTurn`, which owns the
          casing, so a third place cannot get it wrong a third way. */}
      {threadTurn(thread) !== 'closed' ? (
        <OutreachNoReplyNeeded
          outreachId={outreachId}
          onCompleted={() => {
            // Completing changes the thread's TURN, so the list badges and the section it sits under
            // are stale too — not just this screen's copy of the thread.
            void refreshContactData(id);
            void router.push(contactPath(id));
          }}
        />
      ) : null}
    </div>
  );
}

/**
 * The thread's composer.
 *
 * It hands what you type to the MAESTRO rather than sending it to the contact — see
 * `lib/outreach-reply-message.ts` for why. The button says so ("Ask {agent} to reply") so nobody
 * expects the text to leave as-is, which is also what keeps this from reading as the live chat.
 */
function ThreadComposer({ contactName, subject, agentName }: { contactName: string; subject: string; agentName: string }) {
  const { t } = useTranslation();
  const basePath = useBasePath();
  const { logPageClickBi } = useGenericBi();
  const [body, setBody] = useState('');

  const trimmed = body.trim();
  const href = trimmed
    ? chatHandoffLink({ kind: 'new', message: outreachReplyMessage(t, { contactName, subject, body: trimmed }) }, basePath).route
    : '';

  return (
    <div className={styles.composer}>
      <Input value={body} onChange={(event) => setBody(event.target.value)} placeholder={t('contacts.thread.composerPlaceholder')} />
      {trimmed ? (
        <Button href={href} onClick={() => logPageClickBi({ page_name: 'contact_thread', button_name: 'ask_agent_to_reply' })}>
          {t('contacts.thread.composerSend', { agent: agentName })}
        </Button>
      ) : (
        <Button disabled>{t('contacts.thread.composerSend', { agent: agentName })}</Button>
      )}
    </div>
  );
}

/**
 * The files sent with one message.
 *
 * Each is a link to the bytes on Wix media rather than an in-page preview: the viewer already has a
 * document surface, and a thread is a transcript — an inline render of an attached PDF would make
 * the conversation unreadable, which is the same reason the thread got its own screen.
 *
 * Exported so any other thread surface renders attachments the exact same way —
 * one implementation, so the full-page thread and the dialog can never drift on how a file appears.
 */
export function MessageAttachments({ attachments }: { attachments?: WebOutreachAttachment[] }) {
  if (!attachments?.length) return null;
  return (
    <div className={styles.attachments}>
      {attachments.map((attachment, index) => (
        <a
          key={`${attachment.url ?? ''}-${index}`}
          className={styles.attachment}
          href={attachment.url || undefined}
          target="_blank"
          rel="noopener noreferrer"
        >
          <Paperclip size={16} aria-hidden />
          <span className={styles.attachmentName}>{attachmentLabel(attachment)}</span>
        </a>
      ))}
    </div>
  );
}

function ThreadHeader({ thread, name }: { thread: WebOutreachThread; name: string }) {
  const { i18n } = useTranslation();
  // The channel moved into its own coloured chip beside the title, so it is no longer
  // repeated here as grey text.
  const meta = name;

  return (
    <Card variant="outline" size="sm">
      <div className={styles.threadHeaderBlock}>
        {/* Same identity the row that opened this used, so the two read as the same thing — which
            means the subject and its channel chip, not the date the row no longer leads with. */}
        <span className={styles.threadTitleRow}>
          <Typography variant="h4" className={styles.threadTitleText}>
            {threadTitle(thread, i18n.language)}
          </Typography>
          <ChannelChip channel={thread.channel} />
        </span>
        <div className={styles.threadHeadTop}>
          {/* Whose move it is — NOT the raw OutreachStatus this used to print. "Pending" vs "In progress"
              told the reader nothing they could act on, and `outreach-rows.ts`'s own header records why
              it was never even true: "a PENDING thread had the user's reply already sent while an
              IN_PROGRESS one had been stuck for 69 days". Same badge the row that led here wears. */}
          <TurnBadge turn={threadTurn(thread)} />
          <Typography variant="p3" color="secondary">
            {meta}
          </Typography>
        </div>
      </div>
    </Card>
  );
}
